import { Component } from "react";
import ErrorState from "./ErrorState";
import { getNavigationDiagnostics } from "@/lib/navigation-diagnostics";
import UpdateAvailableBanner from "./UpdateAvailableBanner";
import { startVersionCheck } from "@/lib/version-check";

function isChunkLoadError(error) {
  const msg = String(error?.message || "");
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk")
  );
}

export default class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, updateAvailable: false, dismissed: false };
  }

  static getDerivedStateFromError(error) {
    if (isChunkLoadError(error)) {
      // Stale chunk after deploy — never crash the UI. Show a calm
      // "تحديث جديد متاح" banner and let the user reload on their terms.
      return { hasError: false, error: null, updateAvailable: true };
    }
    return { hasError: true, error };
  }

  componentDidMount() {
    // Global listeners: catch chunk-load failures that bubble up *outside*
    // the React render path (e.g. router triggering a lazy import that
    // rejects before any subtree renders). This lets us flip the banner
    // without the error boundary ever unmounting the current screen.
    if (typeof window === "undefined") return;
    this._onError = (event) => {
      if (isChunkLoadError(event?.error || { message: event?.message })) {
        this.setState({ updateAvailable: true });
      }
    };
    this._onRejection = (event) => {
      const reason = event?.reason;
      if (isChunkLoadError(reason)) {
        this.setState({ updateAvailable: true });
        // Prevent noisy "Uncaught (in promise)" log for this benign case.
        if (typeof event.preventDefault === "function") event.preventDefault();
      }
    };
    // Custom event fired by lazyWithRetry() and the version-check poller
    // when a fresh deploy is detected. This is the *primary* path now —
    // chunk-load errors are kept as a defensive fallback.
    this._onUpdateRequired = () => {
      this.setState({ updateAvailable: true });
    };
    window.addEventListener("error", this._onError);
    window.addEventListener("unhandledrejection", this._onRejection);
    window.addEventListener("app:update-required", this._onUpdateRequired);

    // Kick off proactive build-id polling so users learn about a new deploy
    // *before* their next navigation tries to load a stale chunk.
    startVersionCheck();
  }

  componentWillUnmount() {
    if (typeof window === "undefined") return;
    if (this._onError) window.removeEventListener("error", this._onError);
    if (this._onRejection) window.removeEventListener("unhandledrejection", this._onRejection);
    if (this._onUpdateRequired) window.removeEventListener("app:update-required", this._onUpdateRequired);
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) {
      // eslint-disable-next-line no-console
      console.warn("[chunk-reload] stale chunk detected — surfacing update banner:", error?.message);
      return; // Banner handles the rest; do not log as a hard error.
    }
    // [#310-DIAG] Rich diagnostic payload — searchable in console by tag.
    try {
      const nav = getNavigationDiagnostics();
      // eslint-disable-next-line no-console
      console.error("[#310-DIAG]", {
        message: error?.message,
        name: error?.name,
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
        currentUrl: typeof window !== "undefined" ? window.location.href : null,
        previousUrl: nav.previousUrl,
        lastAction: nav.lastAction,
        recentActions: nav.recentActions,
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } catch (logErr) {
      // eslint-disable-next-line no-console
      console.error("[#310-DIAG] failed to assemble payload", logErr, error);
    }
    // Keep original behavior too.
    // eslint-disable-next-line no-console
    console.error("AppErrorBoundary caught:", error, errorInfo);
  }

  handleReset = () => {
    // Full reload — re-rendering the same broken tree just loops the error.
    if (typeof window !== "undefined") {
      window.location.reload();
    } else {
      this.setState({ hasError: false, error: null });
    }
  };

  handleBack = () => {
    if (typeof window === "undefined") return;
    // If we're inside the simulation flow (?simulate=1), going "back" via
    // history would just re-enter simulation. Send the user to the
    // instructor dashboard instead.
    const isSimulation =
      window.location.search.includes("simulate=1") ||
      window.location.pathname.startsWith("/student");
    if (isSimulation) {
      window.location.href = "/instructor/dashboard";
      return;
    }
    window.history.back();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorState
          title="حدث خطأ غير متوقع"
          message="عذراً، حدث خطأ أثناء تحميل الصفحة. يرجى المحاولة مرة أخرى."
          onRetry={this.handleReset}
          onBack={this.handleBack}
        />
      );
    }
    return (
      <>
        {this.props.children}
        {this.state.updateAvailable && !this.state.dismissed && (
          <UpdateAvailableBanner onDismiss={() => this.setState({ dismissed: true })} />
        )}
      </>
    );
  }
}
